/**
 * @file itemAccounts.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-02
 * @modified 2026-10-02
 * @description Which account(s) an item's PURCHASE side posts to, for every
 *              screen that shows it.
 *
 *              Xero gives a tracked (stock) item no purchase account: its
 *              purchases post to its inventory asset account (630 on all of
 *              ours) and COGS takes the cost when it sells. Screens used to put
 *              the COGS code in the purchase slot, or show the slot blank, so
 *              the same item read three ways. Tracked → "Inventory account 630 ·
 *              COGS 310P"; untracked → its purchase account.
 *
 *              The inventory account is read-only here — Xero sets it when the
 *              tracked item is set up; the item sync mirrors it
 *              (inventory.items.inventory_asset_account_code).
 */

/**
 * @returns {Array<{ kind: 'inventory'|'cogs'|'purchase', label: string, short: string, code: string }>}
 */
export function purchaseSideAccounts(item) {
  if (!item) return [];
  const out = [];
  if (item.is_tracked_as_inventory) {
    if (item.inventory_asset_account_code) {
      out.push({ kind: 'inventory', label: 'Inventory account', short: 'INV', code: item.inventory_asset_account_code });
    }
    if (item.cogs_account_code) out.push({ kind: 'cogs', label: 'COGS', short: 'COGS', code: item.cogs_account_code });
  }
  // An untracked item, or a tracked one carrying a purchase account anyway.
  if (item.purchase_account_code && !out.length) {
    out.push({ kind: 'purchase', label: 'Purchase account', short: '', code: item.purchase_account_code });
  }
  return out;
}

/** "Inventory account 630 · COGS 310P", "312", or ''. */
export function purchaseSideText(item) {
  return purchaseSideAccounts(item)
    .map(a => (a.kind === 'purchase' ? a.code : `${a.label} ${a.code}`))
    .join(' · ');
}
